import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { util } from '@joint/plus';
import CloseIcon from '@mui/icons-material/Close';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import * as appShapes from '../../../shapes/app-shapes';
import { refreshOutagesEntity } from '../utils/InspectorUtils';

export const PortsEditor = ({ portsData, updateValue, graph, cell, paper, cellId, readOnly = false }) => {
  const normalizePorts = (ports) =>
    ports.map((p) => ({
      id: p.id,
      group: p.group,
      attrs: p.attrs,
      time: p.time,
      source: p.source ?? null,
      destination: p.destination ?? null,
    }));

  const [localPorts, setLocalPorts] = useState(() =>
    Array.isArray(portsData) && portsData.length > 0
      ? normalizePorts(util.cloneDeep(portsData))
      : [
          {
            id: 'port1',
            group: 'top',
            attrs: { portBody: { fill: '#FFFFFF', stroke: '#000000' } },
            time: 0,
            source: null,
            destination: null,
          },
        ]
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuPortIndex, setMenuPortIndex] = useState(null);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [removeIndex, setRemoveIndex] = useState(null);
  const [picker, setPicker] = useState({ mode: null, index: null });

  const handleAskRemovePort = (index) => {
    setRemoveIndex(index);
    setConfirmRemoveOpen(true);
  };

  const handleConfirmRemove = () => {
    if (removeIndex !== null) {
      deletePort(removeIndex);
    }
    setConfirmRemoveOpen(false);
    setRemoveIndex(null);
  };

  const handleCancelRemove = () => {
    setConfirmRemoveOpen(false);
    setRemoveIndex(null);
  };
  const handleMenuOpen = (event, index) => {
    setAnchorEl(event.currentTarget);
    setMenuPortIndex(index);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuPortIndex(null);
  };

  const handlePortsUpdate = useCallback((ports) => updateValue(ports), [updateValue]);

  // Helper to recompute outages for a set of links (by model or id), tolerant to removed links
  const recomputeOutagesFor = (linkOrId) => {
    if (Array.isArray(linkOrId)) {
      linkOrId.forEach((l) => recomputeOutagesFor(l));
      return;
    }
    const id = typeof linkOrId === 'string' ? linkOrId : linkOrId?.id;
    if (!id) return;
    refreshOutagesEntity(graph, id);
  };

  const insertPortAt = (index) => {
    if (readOnly) return;
    const basePorts = Array.isArray(localPorts) ? localPorts : [];
    const newPort = {
      id: '',
      group: 'auto',
      attrs: { portBody: { fill: '#FFFFFF', stroke: '#000000' } },
      time: 0,
      destination: null,
      source: null,
    };
    // Insert new port at index
    const newPortsArr = [...basePorts.slice(0, index), newPort, ...basePorts.slice(index)];
    // Reassign IDs and get mapping
    const { newPorts, idMap } = reassignPortIdsWithMap(newPortsArr);

    // Update all links that reference the shifted ports and recompute outages
    const changedLinks = [];

    // Inbound links (target is this cell/port)
    graph.getConnectedLinks(cell, { inbound: true }).forEach((link) => {
      const target = link.get('target') || {};
      if (target.port && idMap[target.port] && target.id === cell.id) {
        link.set('target', { ...target, port: idMap[target.port] });
        changedLinks.push(link);
      }
    });
    // Outbound links (source is this cell/port)
    graph.getConnectedLinks(cell, { outbound: true }).forEach((link) => {
      const source = link.get('source') || {};
      if (source.port && idMap[source.port] && source.id === cell.id) {
        link.set('source', { ...source, port: idMap[source.port] });
        changedLinks.push(link);
      }
    });

    recomputeOutagesFor(changedLinks);

    setLocalPorts(newPorts);
    handlePortsUpdate(newPorts);
  };

  const handleInsertAbove = () => {
    if (menuPortIndex != null) insertPortAt(menuPortIndex);
    handleMenuClose();
  };

  const handleInsertBelow = () => {
    if (menuPortIndex != null) insertPortAt(menuPortIndex + 1);
    handleMenuClose();
  };

  // ---- LIVE helpers (use graph directly) ----
  const getCellById = (id) => (id ? graph.getCell(id) : null);
  const getCellName = (id) => {
    const el = getCellById(id);
    return (el?.get && (el.get('cellName') || el.get('name'))) || null;
  };
  const getCellLabel = (id) => {
    const el = getCellById(id);
    return (el?.attr && (el.attr(['label', 'text']) || el.attr('label/text'))) || (id ? `ID: ${id}` : 'None');
  };
  const getPortLabel = (triCell, portId) => {
    if (!triCell || !portId) return null;
    try {
      const p = triCell.getPort(portId);
      return p?.attrs?.portLabel?.text || null;
    } catch (_) {
      return null;
    }
  };
  // -------------------------------------------

  const isFirst = useRef(true);
  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    if (Array.isArray(portsData) && JSON.stringify(portsData) !== JSON.stringify(localPorts)) {
      setLocalPorts(util.cloneDeep(portsData));
    }
  }, [portsData, localPorts]);

  useEffect(() => {
    const handler = () => {
      const current = cell.get('ports/items') || [];
      if (JSON.stringify(current) !== JSON.stringify(localPorts)) {
        setLocalPorts(util.cloneDeep(current));
      }
    };
    cell.on('change:ports', handler);
    graph.on('change:ports', handler);
    return () => {
      cell.off('change:ports', handler);
      graph.off('change:ports', handler);
      // Defensive: if component unmounts while selecting, ensure teardown
      deactivateAllSelections();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cell, graph, localPorts]);

  useEffect(() => {
    const onChangeSource = (link) => {
      if (link?.isLink?.()) recomputeOutagesFor(link);
    };
    const onChangeTarget = (link) => {
      if (link?.isLink?.()) recomputeOutagesFor(link);
    };
    const onAdd = (cellAdded) => {
      if (cellAdded?.isLink?.()) recomputeOutagesFor(cellAdded);
    };
    const onRemove = (cellRemoved) => {
      if (cellRemoved?.isLink?.()) recomputeOutagesFor(cellRemoved.id);
    };

    graph.on('change:source', onChangeSource);
    graph.on('change:target', onChangeTarget);
    graph.on('add', onAdd);
    graph.on('remove', onRemove);

    return () => {
      graph.off('change:source', onChangeSource);
      graph.off('change:target', onChangeTarget);
      graph.off('add', onAdd);
      graph.off('remove', onRemove);
      deactivateAllSelections();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph]);

  const TimeInput = ({ index, value }) => {
    const [inputValue, setInputValue] = useState(String(value));
    const prevValueRef = useRef(String(value));

    useEffect(() => {
      const str = String(value);
      if (str !== prevValueRef.current) {
        setInputValue(str);
        prevValueRef.current = str;
      }
    }, [value]);

    const handleChange = (e) => {
      setInputValue(e.target.value);
    };

    const handleBlur = () => {
      const num = parseFloat(inputValue);
      if (!isNaN(num) && num !== value) {
        const newPorts = localPorts.map((port, i) => (i === index ? { ...port, time: num } : port));
        setLocalPorts(newPorts);
        handlePortsUpdate(newPorts);
      } else {
        setInputValue(String(value));
      }
    };

    return (
      <TextField
        type="number"
        size="small"
        value={inputValue}
        onChange={readOnly ? undefined : handleChange}
        onBlur={readOnly ? undefined : handleBlur}
        disabled={readOnly}
        onWheel={(e) => e.target.blur()}
        inputProps={{
          'data-cy': `port-${index}-time`,
          onCompositionStart: (e) => e.stopPropagation(),
          onCompositionEnd: (e) => e.stopPropagation(),
          inputProps: { min: 0 },
        }}
        InputProps={{
          sx: {
            '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
            '& input[type=number]': {
              MozAppearance: 'textfield', // Firefox
            },
            '& .MuiPopover-root': { zIndex: 9999 },
          },
        }}
      />
    );
  };

  TimeInput.propTypes = {
    index: PropTypes.number.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  };

  const [, setIsSelectingSource] = useState(false);
  const [, setSelectedSourcePortIndex] = useState(null);

  // ensure only one picker can ever be active (last action wins)
  const deactivateAllSelections = () => {
    try {
      if (paper?._sourceSelectionCallback) {
        paper.off('element:pointerclick', paper._sourceSelectionCallback);
        paper.off('element:magnet:pointerclick', paper._sourceSelectionCallback);
        paper._sourceSelectionCallback = null;
      }
      if (paper?._destinationSelectionCallback) {
        paper.off('element:pointerclick', paper._destinationSelectionCallback);
        paper.off('element:magnet:pointerclick', paper._destinationSelectionCallback);
        paper._destinationSelectionCallback = null;
      }
    } catch (_) {
      /* no-op */
    }
    setIsSelectingSource(false);
    setSelectedSourcePortIndex(null);
    setIsSelectingDestination(false);
    setSelectedPortIndex(null);
    if (paper?.el?.style) paper.el.style.cursor = 'default';
    setPicker({ mode: null, index: null });
  };

  // Support clicking on a port (magnet) to capture source port id
  const activateSourceSelection = (index) => {
    if (readOnly) return;
    // Always cancel any active selection first (destination or source)
    deactivateAllSelections();
    setPicker({ mode: 'source', index });
    setIsSelectingSource(true);
    setSelectedSourcePortIndex(index);
    if (paper?.el?.style) paper.el.style.cursor = 'crosshair';

    const handleClick = (cellView, evt) => {
      const targetCell = cellView.model;
      if (targetCell.id === cellId) return;

      const maybePort = evt?.target?.getAttribute?.('port') || null;
      handleSourceChange(index, targetCell.id, maybePort);
      deactivateSourceSelection();
    };

    paper.on('element:pointerclick', handleClick);
    paper.on('element:magnet:pointerclick', handleClick);
    paper._sourceSelectionCallback = handleClick;
  };

  const deactivateSourceSelection = () => {
    // Route through centralized teardown to guarantee exclusivity
    deactivateAllSelections();
  };

  const handleSourceChange = (index, newValue, sourcePortId = null) => {
    if (readOnly) return;

    const port = localPorts[index];
    if (!port) return;

    const existingLink = graph.getConnectedLinks(cell, { inbound: true }).find((link) => {
      const tgt = link.get('target');
      return tgt && tgt.id === cell.id && tgt.port === port.id;
    });

    if (existingLink) {
      if (newValue) {
        const prevSource = existingLink.get('source') || {};
        const updatedSource = { ...prevSource, id: newValue };
        if (sourcePortId) {
          updatedSource.port = sourcePortId; // use clicked source port
        } else if (updatedSource.port) {
          // if switching away from a ported source (clicked on body), drop port
          delete updatedSource.port;
        }
        existingLink.set('source', updatedSource);
        existingLink.set('portsEditor', true, { silent: true });
        recomputeOutagesFor(existingLink);
      } else {
        const removedId = existingLink.id;
        existingLink.remove();
        recomputeOutagesFor(removedId);
      }
    } else if (newValue) {
      const link = new appShapes.app.Link({
        source: sourcePortId ? { id: newValue, port: sourcePortId } : { id: newValue },
        target: { id: cell.id, port: port.id },
        router: { name: 'normal' },
      });
      link.addTo(graph);
      link.set('portsEditor', true, { silent: true });
      recomputeOutagesFor(link);
    }

    const updated = localPorts.map((p, i) => (i === index ? { ...p, source: newValue || null } : p));
    setLocalPorts(updated);
    handlePortsUpdate(updated);
  };

  const handleDestinationChange = (index, newValue, targetPortId = null) => {
    if (readOnly) return;

    const port = localPorts[index];
    if (!port) return;

    const existingLink = graph.getConnectedLinks(cell, { outbound: true }).find((link) => {
      const src = link.get('source');
      return src && src.id === cell.id && src.port === port.id;
    });

    if (existingLink) {
      if (newValue) {
        const prevTarget = existingLink.get('target') || {};
        const updatedTarget = { ...prevTarget, id: newValue };
        if (targetPortId) {
          updatedTarget.port = targetPortId; // use clicked destination port
        } else if (updatedTarget.port) {
          // if switching away from a ported target (clicked on body), drop port
          delete updatedTarget.port;
        }
        existingLink.set('target', updatedTarget);
        existingLink.set('portsEditor', true, { silent: true });
        recomputeOutagesFor(existingLink);
      } else {
        const removedId = existingLink.id;
        existingLink.remove();
        recomputeOutagesFor(removedId);
      }
    } else if (newValue) {
      const link = new appShapes.app.Link({
        source: { id: cell.id, port: port.id },
        target: targetPortId ? { id: newValue, port: targetPortId } : { id: newValue },
        router: { name: 'normal' },
      });
      link.addTo(graph);
      link.set('portsEditor', true, { silent: true });
      recomputeOutagesFor(link);
    }

    const newPorts = localPorts.map((p, i) => (i === index ? { ...p, destination: newValue || null } : p));
    setLocalPorts(newPorts);
    handlePortsUpdate(newPorts);
  };

  const [, setIsSelectingDestination] = useState(false);
  const [, setSelectedPortIndex] = useState(null);

  // listen to both element and magnet clicks to capture port id
  const activateDestinationSelection = (index) => {
    if (readOnly) return;
    // Always cancel any active selection first (source or destination)
    deactivateAllSelections();
    setPicker({ mode: 'destination', index });
    setIsSelectingDestination(true);
    setSelectedPortIndex(index);
    if (paper?.el?.style) paper.el.style.cursor = 'crosshair';

    const handleClick = (cellView, evt) => {
      const targetCell = cellView.model;
      if (targetCell.id === cellId) return;

      const maybePort = evt?.target?.getAttribute?.('port') || null;
      handleDestinationChange(index, targetCell.id, maybePort);
      deactivateDestinationSelection();
    };

    paper.on('element:pointerclick', handleClick);
    paper.on('element:magnet:pointerclick', handleClick);
    paper._destinationSelectionCallback = handleClick;
  };

  const deactivateDestinationSelection = () => {
    // Route through centralized teardown to guarantee exclusivity
    deactivateAllSelections();
  };

  function reassignPortIdsWithMap(ports) {
    const idMap = {};
    const newPorts = ports.map((port, idx) => {
      const newId = `port${idx + 1}`;
      idMap[port.id] = newId;
      return {
        ...port,
        id: newId,
        attrs: {
          ...port.attrs,
          portLabel: { ...(port.attrs?.portLabel || {}), text: `${idx + 1}` },
        },
      };
    });
    return { newPorts, idMap };
  }

  const addPort = () => {
    if (readOnly) return;
    const newPort = {
      id: '',
      group: 'auto',
      attrs: { portBody: { fill: '#FFFFFF', stroke: '#000000' } },
      time: 0,
      destination: null,
      source: null,
    };
    const basePorts = Array.isArray(localPorts) ? localPorts : [];
    const { newPorts } = reassignPortIdsWithMap([...basePorts, newPort]);
    setLocalPorts(newPorts);
    handlePortsUpdate(newPorts);
  };

  const deletePort = (index) => {
    if (readOnly) return;
    const portToDelete = localPorts[index];
    if (!portToDelete) return;

    const toRemove = [];
    const links = [
      ...graph.getConnectedLinks(cell, { inbound: true }),
      ...graph.getConnectedLinks(cell, { outbound: true }),
    ];
    links.forEach((link) => {
      const source = link.get('source') || {};
      const target = link.get('target') || {};
      if ((source.port && source.port === portToDelete.id) || (target.port && target.port === portToDelete.id)) {
        toRemove.push(link.id);
        link.remove();
      }
    });
    recomputeOutagesFor(toRemove);

    const filteredPorts = localPorts.filter((_, i) => i !== index);
    const { newPorts, idMap } = reassignPortIdsWithMap(filteredPorts);

    const changedLinks = [];

    graph.getConnectedLinks(cell, { inbound: true }).forEach((link) => {
      const target = link.get('target') || {};
      if (target.port && idMap[target.port] && target.id === cell.id) {
        link.set('target', { ...target, port: idMap[target.port] });
        changedLinks.push(link);
      }
    });
    graph.getConnectedLinks(cell, { outbound: true }).forEach((link) => {
      const source = link.get('source') || {};
      if (source.port && idMap[source.port] && source.id === cell.id) {
        link.set('source', { ...source, port: idMap[source.port] });
        changedLinks.push(link);
      }
    });

    recomputeOutagesFor(changedLinks);

    setLocalPorts(newPorts);
    handlePortsUpdate(newPorts);
  };

  return (
    <Box
      sx={{
        width: '100%',
        position: 'relative',
        zIndex: 1,
        maxHeight: '435px',
        overflowY: 'auto',
        overflowX: 'visible',
      }}
    >
      <TableContainer
        elevation={4}
        component={Paper}
        sx={{ overflow: 'visible', height: '100%' }}
        data-cy="ports-editor"
      >
        <Table size="small" sx={{ '& .MuiTableCell-root': { px: 1, py: '6px' } }}>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Durée (s)</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Destination</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ overflowY: 'scroll', maxHeight: '435px' }}>
            {localPorts.map((port, index) => {
              // inbound link (to this cell/port)
              const inbound = graph.getConnectedLinks(cell, { inbound: true }).find((lnk) => {
                const tgt = lnk.get('target') || {};
                return tgt.id === cell.id && tgt.port === port.id;
              });
              const inboundSrc = inbound?.get('source') || null;
              const srcId = inboundSrc?.id || null;
              const srcLabel = srcId ? getCellLabel(srcId) : 'None';
              const srcName = srcId ? getCellName(srcId) : null;
              const srcPortDisplay =
                inboundSrc?.port && getPortLabel(getCellById(srcId), inboundSrc.port)
                  ? `port ${getPortLabel(getCellById(srcId), inboundSrc.port)}`
                  : inboundSrc?.port
                    ? `port ${inboundSrc.port}`
                    : '';

              // outbound link (from this cell/port)
              const outbound = graph.getConnectedLinks(cell, { outbound: true }).find((lnk) => {
                const src = lnk.get('source') || {};
                return src.id === cell.id && src.port === port.id;
              });
              const outboundTgt = outbound?.get('target') || null;
              const destId = outboundTgt?.id || null;
              const destLabel = destId ? getCellLabel(destId) : 'None';
              const destName = destId ? getCellName(destId) : null;
              const destPortDisplay =
                outboundTgt?.port && getPortLabel(getCellById(destId), outboundTgt.port)
                  ? `port ${getPortLabel(getCellById(destId), outboundTgt.port)}`
                  : outboundTgt?.port
                    ? `port ${outboundTgt.port}`
                    : '';

              return (
                <TableRow key={port.id || index}>
                  <TableCell width="10px">{index + 1}</TableCell>
                  <TableCell>
                    <TimeInput index={index} value={port.time} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FormControl fullWidth size="small">
                        {/* Label visible; Name as tooltip */}
                        <Tooltip
                          title={srcName || ''}
                          disableHoverListener={!srcName}
                          arrow
                          componentsProps={{
                            popper: {
                              disablePortal: true,
                            },
                          }}
                          placement="top"
                        >
                          <span>{srcLabel}</span>
                        </Tooltip>
                        <span style={{ opacity: 0.7 }}>{srcPortDisplay}</span>
                      </FormControl>
                      {!port.destination && !(picker.mode === 'destination' && picker.index === index) && (
                        <IconButton
                          color="primary"
                          onClick={readOnly ? undefined : () => activateSourceSelection(index)}
                          size="small"
                          sx={{ ml: 1 }}
                          data-cy={`port-${index}-source-selector`}
                          disabled={readOnly || (picker.mode && picker.index !== index)}
                        >
                          <GpsFixedIcon style={{ color: '#a4e4af' }} />
                        </IconButton>
                      )}
                      {picker.mode === 'source' && picker.index === index && (
                        <Tooltip title="Annuler la sélection de la source" placement="top" arrow>
                          <span>
                            <IconButton
                              size="small"
                              onClick={deactivateSourceSelection}
                              data-cy={`port-${index}-source-cancel`}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FormControl fullWidth size="small">
                        <Tooltip
                          title={destName || ''}
                          disableHoverListener={!destName}
                          arrow
                          componentsProps={{
                            popper: {
                              disablePortal: true,
                            },
                          }}
                          placement="top"
                        >
                          <span>{destLabel}</span>
                        </Tooltip>
                        <span style={{ opacity: 0.7 }}>{destPortDisplay}</span>
                      </FormControl>
                      {!port.source && !(picker.mode === 'source' && picker.index === index) && (
                        <IconButton
                          color="primary"
                          onClick={readOnly ? undefined : () => activateDestinationSelection(index)}
                          size="small"
                          sx={{ ml: 1 }}
                          data-cy={`port-${index}-target-selector`}
                          disabled={readOnly || (picker.mode && picker.index !== index)}
                        >
                          <GpsFixedIcon />
                        </IconButton>
                      )}
                      {picker.mode === 'destination' && picker.index === index && (
                        <Tooltip title="Annuler la sélection de la destination" placement="top" arrow>
                          <span>
                            <IconButton
                              size="small"
                              onClick={deactivateDestinationSelection}
                              data-cy={`port-${index}-target-cancel`}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.2 }}>
                      <IconButton
                        onClick={readOnly ? undefined : (e) => handleMenuOpen(e, index)}
                        size="small"
                        disabled={readOnly}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} disablePortal>
        <MenuItem
          onClick={() => {
            handleAskRemovePort(menuPortIndex);
            handleMenuClose();
          }}
          disabled={readOnly}
        >
          Supprimer le port et le transport associé
        </MenuItem>
        <MenuItem onClick={handleInsertAbove} disabled={readOnly}>
          Insérer un nouveau port avant
        </MenuItem>
        <MenuItem onClick={handleInsertBelow} disabled={readOnly}>
          Insérer un nouveau port après
        </MenuItem>
      </Menu>
      <Dialog open={confirmRemoveOpen} onClose={handleCancelRemove} disablePortal>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          Supprimer ce port supprimera aussi le transport associé ainsi que le temps entre ce port et le suivant sur
          l’anneau de tri
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRemove}>Annuler</Button>
          <Button onClick={handleConfirmRemove} color="error" autoFocus>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          variant="contained"
          onClick={readOnly ? undefined : addPort}
          data-cy="add-port-button"
          sx={{ zIndex: 1 }}
          disabled={readOnly}
        >
          Ajouter un port
        </Button>
      </Box>
    </Box>
  );
};

PortsEditor.propTypes = {
  portsData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      group: PropTypes.string.isRequired,
      attrs: PropTypes.shape({
        portBody: PropTypes.shape({
          fill: PropTypes.string,
          stroke: PropTypes.string,
        }),
      }).isRequired,
      time: PropTypes.number.isRequired,
      source: PropTypes.string,
      destination: PropTypes.string,
    })
  ),
  updateValue: PropTypes.func.isRequired,
  cell: PropTypes.shape({
    id: PropTypes.string.isRequired,
    get: PropTypes.func.isRequired,
    on: PropTypes.func.isRequired,
    off: PropTypes.func.isRequired,
  }).isRequired,
  graph: PropTypes.shape({
    getElements: PropTypes.func.isRequired,
    getCell: PropTypes.func.isRequired,
    getConnectedLinks: PropTypes.func.isRequired,
    on: PropTypes.func.isRequired,
    off: PropTypes.func.isRequired,
  }).isRequired,
  paper: PropTypes.shape({
    el: PropTypes.shape({ style: PropTypes.object }).isRequired,
    on: PropTypes.func.isRequired,
    off: PropTypes.func.isRequired,
    _destinationSelectionCallback: PropTypes.func,
    _sourceSelectionCallback: PropTypes.func,
  }).isRequired,
  cellId: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
};
